import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Input, Modal, notification, Spin, Table, Tag, Tooltip, Typography } from 'antd';
import { Price } from '@partsbadger/library';
import { get } from '@partsbadger/utils';
import QuoteStore from '../stores/QuoteStore';
import ComparePartsModal, { CompareParty } from './ComparePartsModal';
import dayjs from 'dayjs';

interface MatchedPartsModalProps {
    open: boolean;
    onClose: () => void;
    masterProductId: number | undefined;
    currentMpId: number | undefined;
    currentPart?: CompareParty | null;
}

const PAGE_SIZE = 10;
const DEBOUNCE_MS = 400;
const MATCH_REASON_LABELS: Record<string, string> = {
    same_step_different_drawing: 'Same 3D file; 2D drawing differs',
    similar_geometry: 'Similar dimensions and volume',
    revision_chain: 'Same revision family',
    part_name: 'Same base part name',
};

const MatchedPartsModal: React.FC<MatchedPartsModalProps> = ({
    open,
    onClose,
    masterProductId,
    currentMpId,
    currentPart = null,
}) => {
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [compareRecord, setCompareRecord] = useState<any | null>(null);
    const [fetchedCurrent, setFetchedCurrent] = useState<CompareParty | null>(null);

    const [results, setResults] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [exactCount, setExactCount] = useState(0);
    const [similarCount, setSimilarCount] = useState(0);
    const [nameCount, setNameCount] = useState(0);

    const [searchText, setSearchText] = useState('');
    const [materialSearch, setMaterialSearch] = useState('');
    const [finishSearch, setFinishSearch] = useState('');
    const [coatingSearch, setCoatingSearch] = useState('');
    const [ordering, setOrdering] = useState('-completed_at');

    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const quotedProductIdsInCurrentQuote = useMemo(
        () =>
            new Set(
                (QuoteStore.quote?.line_items ?? [])
                    .flatMap(lineItem => [
                        lineItem.duplicated_from as unknown,
                        lineItem.already_quote_product as unknown,
                    ])
                    .filter((quotedProductId): quotedProductId is number => typeof quotedProductId === 'number')
            ),
        [QuoteStore.quote?.line_items]
    );
    const masterProductIdsInCurrentQuote = useMemo(
        () =>
            new Set(
                (QuoteStore.quote?.line_items ?? [])
                    .flatMap(lineItem => [lineItem.product?.master_product, lineItem.master_product_details?.id])
                    .filter((masterProductId): masterProductId is number => typeof masterProductId === 'number')
            ),
        [QuoteStore.quote?.line_items]
    );
    const isAlreadyInCurrentQuote = (record: { id: number; master_product_id?: number }) =>
        quotedProductIdsInCurrentQuote.has(record.id) ||
        (typeof record.master_product_id === 'number' && masterProductIdsInCurrentQuote.has(record.master_product_id));

    const buildUrl = useCallback(
        (page: number, search: string, material: string, finish: string, coating: string, order: string) => {
            const params = new URLSearchParams();
            params.set('page', String(page));
            params.set('page_size', String(PAGE_SIZE));
            if (search) params.set('search', search);
            if (material) params.set('material', material);
            if (finish) params.set('finish', finish);
            if (coating) params.set('coating', coating);
            if (order) params.set('ordering', order);
            return `/staff/master-products/${masterProductId}/previous-quotes/?${params.toString()}`;
        },
        [masterProductId]
    );

    const fetchPage = useCallback(
        (page: number, search: string, material: string, finish: string, coating: string, order: string) => {
            if (!masterProductId) return;
            setLoading(true);
            get(buildUrl(page, search, material, finish, coating, order))
                .then((res: any) => {
                    setResults(res.results || []);
                    setTotal(res.count || 0);
                    setCurrentPage(res.page || 1);
                    setExactCount(res.exact_count || 0);
                    setSimilarCount(res.similar_count || 0);
                    setNameCount(res.name_count || 0);
                })
                .finally(() => setLoading(false));
        },
        [masterProductId, buildUrl]
    );

    useEffect(() => {
        if (open && masterProductId) {
            setCurrentPage(1);
            fetchPage(1, '', '', '', '', '-completed_at');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, masterProductId]);

    useEffect(() => {
        if (!open || !currentMpId || currentPart) {
            return;
        }
        let cancelled = false;
        get(`/staff/master-products/${currentMpId}/`)
            .then((mp: any) => {
                if (cancelled) return;
                const sf = mp?.step_file;
                const dims =
                    sf?.bb_x && sf?.bb_y && sf?.bb_z
                        ? `${Number(sf.bb_x).toFixed(3)} × ${Number(sf.bb_y).toFixed(3)} × ${Number(sf.bb_z).toFixed(
                              3
                          )}`
                        : null;
                setFetchedCurrent({
                    name: mp?.name || '',
                    step_file_3djs_url: sf?.file_3djs || null,
                    step_file_url: sf?.file_obj || sf?.file || null,
                    drawing_file_url: mp?.drawing_file?.file || null,
                    dimensions: dims,
                });
            })
            .catch(() => {
                if (!cancelled) setFetchedCurrent(null);
            });
        return () => {
            cancelled = true;
        };
    }, [open, currentMpId, currentPart]);

    const effectiveCurrent: CompareParty | null = currentPart ?? fetchedCurrent;

    const debouncedFetch = (search: string, material: string, finish: string, coating: string) => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setCurrentPage(1);
            fetchPage(1, search, material, finish, coating, ordering);
        }, DEBOUNCE_MS);
    };

    const handleSearchChange = (value: string) => {
        setSearchText(value);
        debouncedFetch(value, materialSearch, finishSearch, coatingSearch);
    };

    const handleMaterialChange = (value: string) => {
        setMaterialSearch(value);
        debouncedFetch(searchText, value, finishSearch, coatingSearch);
    };

    const handleFinishChange = (value: string) => {
        setFinishSearch(value);
        debouncedFetch(searchText, materialSearch, value, coatingSearch);
    };

    const handleCoatingChange = (value: string) => {
        setCoatingSearch(value);
        debouncedFetch(searchText, materialSearch, finishSearch, value);
    };

    const handleClearFilters = () => {
        setSearchText('');
        setMaterialSearch('');
        setFinishSearch('');
        setCoatingSearch('');
        setCurrentPage(1);
        fetchPage(1, '', '', '', '', ordering);
    };

    const hasActiveFilters = searchText || materialSearch || finishSearch || coatingSearch;

    const handleClose = () => {
        setSelectedRowKeys([]);
        setSearchText('');
        setMaterialSearch('');
        setFinishSearch('');
        setCoatingSearch('');
        setOrdering('-completed_at');
        setResults([]);
        setTotal(0);
        onClose();
    };

    const handleBulkAdd = async () => {
        if (!currentMpId) return;
        setActionLoading(true);
        try {
            await QuoteStore.createLineItemFromExistingLineItems(selectedRowKeys as number[]);
            await QuoteStore.deleteMasterProduct(currentMpId);
            handleClose();
            notification.success({
                message: 'Parts added',
                description: `${selectedRowKeys.length} part(s) added to this quote.`,
            });
        } catch {
            notification.error({
                message: 'Error',
                description: 'Failed to add parts. Please try again.',
            });
        } finally {
            setActionLoading(false);
        }
    };

    const handleUseThisPart = async (recordId: number) => {
        if (!currentMpId) return;
        setActionLoading(true);
        try {
            await QuoteStore.createLineItemFromExistingLineItems([recordId]);
            await QuoteStore.deleteMasterProduct(currentMpId);
            handleClose();
            notification.success({
                message: 'Part replaced',
                description: 'The previously quoted part has been added to this quote.',
            });
        } catch {
            notification.error({
                message: 'Error',
                description: 'Failed to replace part. Please try again.',
            });
        } finally {
            setActionLoading(false);
        }
    };

    const handleTableChange = (pagination: any, _filters: any, sorter: any) => {
        let newOrdering = ordering;
        if (sorter && sorter.field === 'quote_name') {
            newOrdering = sorter.order === 'ascend' ? 'completed_at' : '-completed_at';
            setOrdering(newOrdering);
        }
        const newPage = pagination?.current || 1;
        setCurrentPage(newPage);
        fetchPage(newPage, searchText, materialSearch, finishSearch, coatingSearch, newOrdering);
    };

    const columns = [
        {
            title: 'Preview',
            dataIndex: 'step_file_image',
            width: 65,
            render: (img: string) =>
                img ? (
                    <img
                        src={img}
                        style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4, border: '1px solid #eee' }}
                    />
                ) : (
                    <div
                        style={{
                            width: 50,
                            height: 50,
                            borderRadius: 4,
                            background: '#f5f5f5',
                            border: '1px solid #eee',
                        }}
                    />
                ),
        },
        {
            title: 'Part',
            dataIndex: 'part_name',
            ellipsis: true,
            render: (_: any, record: any) => (
                <div style={{ lineHeight: 1.4 }}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{record.part_name}</div>
                    <div style={{ fontSize: 11, color: '#999' }}>
                        {Number(record.part_length).toFixed(3)} × {Number(record.part_width).toFixed(3)} ×{' '}
                        {Number(record.part_height).toFixed(3)} {record.part_unit}
                    </div>
                </div>
            ),
        },
        {
            title: 'Specs',
            dataIndex: 'material_name',
            width: 150,
            render: (_: any, record: any) => {
                const specs = [record.material_name, record.finish_name, record.coating_name].filter(Boolean);
                if (specs.length === 0) return <span style={{ color: '#ccc' }}>—</span>;
                return (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                        {record.material_name && (
                            <Tag style={{ fontSize: 11, margin: 0, lineHeight: '18px' }}>{record.material_name}</Tag>
                        )}
                        {record.finish_name && (
                            <Tag style={{ fontSize: 11, margin: 0, lineHeight: '18px' }}>{record.finish_name}</Tag>
                        )}
                        {record.coating_name && (
                            <Tag style={{ fontSize: 11, margin: 0, lineHeight: '18px' }}>{record.coating_name}</Tag>
                        )}
                    </div>
                );
            },
        },
        {
            title: 'Quote',
            dataIndex: 'quote_name',
            width: 160,
            sorter: true,
            defaultSortOrder: 'descend' as const,
            render: (_: any, record: any) => (
                <div style={{ lineHeight: 1.4 }}>
                    <Typography.Link
                        href={`/quotes/${record.quote_id}/details/`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontSize: 13 }}
                    >
                        {record.quote_name || `Quote #${record.quote_id}`}
                    </Typography.Link>
                    <div style={{ fontSize: 11, color: '#999' }}>
                        {record.completed_at
                            ? dayjs(record.completed_at).format('MM/DD/YYYY')
                            : dayjs(record.quoted_date).format('MM/DD/YYYY')}
                    </div>
                    {(record.quote_is_dynamic || record.quote_type === 'Requote') && (
                        <div style={{ marginTop: 2 }}>
                            {record.quote_is_dynamic && (
                                <Tag
                                    color="purple"
                                    style={{ fontSize: 10, lineHeight: '16px', margin: 0, marginRight: 3 }}
                                >
                                    Dynamic
                                </Tag>
                            )}
                            {record.quote_type === 'Requote' && (
                                <Tag color="magenta" style={{ fontSize: 10, lineHeight: '16px', margin: 0 }}>
                                    Requote
                                </Tag>
                            )}
                        </div>
                    )}
                </div>
            ),
        },
        {
            title: 'Qty',
            dataIndex: 'quantity',
            width: 50,
            align: 'center' as const,
            render: (qty: number) => <span style={{ fontSize: 13 }}>{qty}</span>,
        },
        {
            title: 'Unit Price',
            dataIndex: 'unit_price',
            width: 90,
            align: 'right' as const,
            render: (price: number) => (
                <span style={{ fontSize: 13, fontWeight: 500 }}>
                    <Price value={price} />
                </span>
            ),
        },
        {
            title: 'Match',
            dataIndex: '_match_type',
            width: 155,
            align: 'center' as const,
            render: (t: string, record: any) => (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    {t === 'exact' ? (
                        <Tag color="green" style={{ margin: 0 }}>
                            Exact
                        </Tag>
                    ) : t === 'similar' ? (
                        <Tag color="blue" style={{ margin: 0 }}>
                            Similar
                        </Tag>
                    ) : (
                        <Tag color="orange" style={{ margin: 0 }}>
                            Name
                        </Tag>
                    )}
                    {record._match_reason ? (
                        <span style={{ color: '#8c8c8c', fontSize: 10, lineHeight: '13px', textAlign: 'center' }}>
                            {MATCH_REASON_LABELS[record._match_reason]}
                        </span>
                    ) : null}
                    <Typography.Link
                        data-testid={`otf-previously-quoted-compare-${record.id}`}
                        style={{ fontSize: 11 }}
                        onClick={() => setCompareRecord(record)}
                    >
                        Compare
                    </Typography.Link>
                </div>
            ),
        },
        {
            title: '',
            key: 'action',
            width: 90,
            render: (_: any, record: any) =>
                isAlreadyInCurrentQuote(record) ? (
                    <Tooltip title="Already in this quote">
                        <Tag
                            color="green"
                            style={{ margin: 0, whiteSpace: 'nowrap' }}
                            data-testid={`otf-previously-quoted-in-quote-${record.id}`}
                        >
                            In quote
                        </Tag>
                    </Tooltip>
                ) : (
                    <Tooltip title="Replace current part with this previously quoted part">
                        <Button
                            data-testid={`otf-previously-quoted-requote-${record.id}`}
                            type="primary"
                            size="small"
                            ghost
                            loading={actionLoading}
                            onClick={() => handleUseThisPart(record.id)}
                            style={{ fontSize: 12 }}
                        >
                            Requote
                        </Button>
                    </Tooltip>
                ),
        },
    ];

    return (
        <>
            <style>{`
                .matched-parts-table .ant-table-tbody > tr > td {
                    padding: 10px 8px;
                    font-size: 13px;
                    vertical-align: middle;
                    border-bottom: 1px solid #f5f5f5;
                }
                .matched-parts-table .ant-table-tbody > tr:hover > td {
                    background: #fafbfc !important;
                }
                .matched-parts-table .ant-table-thead > tr > th {
                    font-size: 11px;
                    text-transform: uppercase;
                    letter-spacing: 0.3px;
                    color: #888;
                    padding: 8px 8px;
                    background: #fafafa;
                }
            `}</style>
            <Modal
                data-testid="otf-previously-quoted-modal"
                title={
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                        <span style={{ fontSize: 16, fontWeight: 600 }}>Matched Parts</span>
                        {total > 0 && (
                            <span style={{ fontSize: 12, color: '#999', fontWeight: 400 }}>
                                {exactCount} exact · {similarCount} similar
                                {nameCount > 0 ? ` · ${nameCount} by name` : ''}
                            </span>
                        )}
                    </div>
                }
                open={open}
                onCancel={handleClose}
                footer={
                    selectedRowKeys.length > 0 ? (
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                                data-testid="otf-previously-quoted-add-selected"
                                type="primary"
                                loading={actionLoading}
                                onClick={handleBulkAdd}
                            >
                                Add {selectedRowKeys.length} Part{selectedRowKeys.length !== 1 ? 's' : ''}
                            </Button>
                        </div>
                    ) : null
                }
                width="min(1100px, 92vw)"
                bodyStyle={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto', padding: '16px 20px' }}
            >
                {!loading && total > 0 && exactCount === 0 && similarCount === 0 && nameCount > 0 && (
                    <Alert
                        type="warning"
                        showIcon
                        style={{ marginBottom: 14 }}
                        message="These may be revisions of this part"
                        description="No exact or geometry match was found. The results below share a similar part name and may be earlier revisions. Review carefully before re-quoting."
                    />
                )}

                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 14 }}>
                    <Input.Search
                        data-testid="otf-previously-quoted-filter-name"
                        placeholder="Part name"
                        allowClear
                        size="small"
                        style={{ width: 150 }}
                        value={searchText}
                        onChange={e => handleSearchChange(e.target.value)}
                    />
                    <Input.Search
                        data-testid="otf-previously-quoted-filter-material"
                        placeholder="Material"
                        allowClear
                        size="small"
                        style={{ width: 120 }}
                        value={materialSearch}
                        onChange={e => handleMaterialChange(e.target.value)}
                    />
                    <Input.Search
                        data-testid="otf-previously-quoted-filter-finish"
                        placeholder="Finish"
                        allowClear
                        size="small"
                        style={{ width: 120 }}
                        value={finishSearch}
                        onChange={e => handleFinishChange(e.target.value)}
                    />
                    <Input.Search
                        data-testid="otf-previously-quoted-filter-coating"
                        placeholder="Coating"
                        allowClear
                        size="small"
                        style={{ width: 120 }}
                        value={coatingSearch}
                        onChange={e => handleCoatingChange(e.target.value)}
                    />
                    {hasActiveFilters && (
                        <Button
                            data-testid="otf-previously-quoted-clear-filters"
                            size="small"
                            type="link"
                            onClick={handleClearFilters}
                        >
                            Clear
                        </Button>
                    )}
                </div>

                <Spin spinning={loading}>
                    <Table
                        data-testid="otf-previously-quoted-results"
                        rowKey="id"
                        size="middle"
                        className="matched-parts-table"
                        pagination={{
                            current: currentPage,
                            pageSize: PAGE_SIZE,
                            total,
                            showSizeChanger: false,
                            size: 'small',
                        }}
                        onChange={handleTableChange}
                        rowSelection={{
                            selectedRowKeys,
                            onChange: keys => setSelectedRowKeys(keys),
                            getCheckboxProps: (record: any) => ({
                                disabled: isAlreadyInCurrentQuote(record),
                            }),
                        }}
                        onRow={(record: any) =>
                            ({
                                'data-testid': `otf-previously-quoted-row-${record.id}`,
                            } as any)
                        }
                        dataSource={results}
                        columns={columns}
                        style={{ fontSize: 13 }}
                    />
                </Spin>
            </Modal>

            <ComparePartsModal
                open={!!compareRecord}
                onClose={() => setCompareRecord(null)}
                current={effectiveCurrent}
                matched={
                    compareRecord
                        ? {
                              name: compareRecord.part_name,
                              step_file_3djs_url: compareRecord.step_file_3djs_url,
                              step_file_url: compareRecord.step_file_url,
                              drawing_file_url: compareRecord.drawing_file_url,
                              dimensions:
                                  compareRecord.part_length && compareRecord.part_width && compareRecord.part_height
                                      ? `${Number(compareRecord.part_length).toFixed(3)} × ${Number(
                                            compareRecord.part_width
                                        ).toFixed(3)} × ${Number(compareRecord.part_height).toFixed(3)} ${
                                            compareRecord.part_unit || ''
                                        }`.trim()
                                      : null,
                          }
                        : null
                }
            />
        </>
    );
};

export default MatchedPartsModal;
